import React, { useState, useContext, useEffect, useCallback } from "react";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";
import DetailHeader from "../../components/DetailHeader";
import LicenseErrorModal from "../../components/LicenseErrorModal";
import Validation from "../../../../common/Validation";
import PerCompanyConfig from "./PerCompanyConfig";
import "./style.css";

export default function UserForm({ user, onSave, onCancel }) {
  const {
    createUser,
    updateUser,
    fetchLicenses,
    fetchGroupsForCompany,
    companies,
    roles,
    users,
    licenses,
  } = useContext(FieldConfigurationContext);

  const isEditMode = !!user;
  const configureUsersData = companies?.configureUsersData || {};
  const manageableUuidsList = Object.keys(configureUsersData);
  const showCompanySection = manageableUuidsList.length > 1;

  // Personal info state
  const [personalInfo, setPersonalInfo] = useState({
    email: user?.email || "",
    mobileNumber: user?.mobileNumber || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
  });

  // Per-company configuration state
  // { [companyUuid]: { assigned, userUuid, employee, roleUuid, groups, enabled, availableGroups, groupsLoading } }
  const [companyConfigs, setCompanyConfigs] = useState(() => {
    const configs = {};
    for (const companyUuid of manageableUuidsList) {
      const userIdx = user?.companyUuids?.indexOf(companyUuid) ?? -1;
      const existingUserUuid = userIdx >= 0 ? user?.uuids?.[userIdx] : null;

      // Find the individual user record for this company
      const companyUserRecord =
        existingUserUuid && users?.data
          ? users.data.find((u) => u.uuid === existingUserUuid)
          : null;

      configs[companyUuid] = {
        assigned: !!existingUserUuid,
        userUuid: existingUserUuid || null,
        employee: companyUserRecord?.employee || companyUserRecord?.code || "",
        roleUuid: companyUserRecord?.roleUuid || companyUserRecord?.role || "",
        groups: companyUserRecord?.groups || [],
        enabled: companyUserRecord?.active !== false,
        availableGroups: [],
        groupsLoading: false,
      };
    }
    // Single-company mode: always assigned
    if (!showCompanySection && manageableUuidsList.length === 1) {
      const companyUuid = manageableUuidsList[0];
      configs[companyUuid] = {
        ...configs[companyUuid],
        assigned: true,
      };
    }
    return configs;
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [licenseErrors, setLicenseErrors] = useState([]);
  const [licenseErrorDetails, setLicenseErrorDetails] = useState([]);
  const [showLicenseModal, setShowLicenseModal] = useState(false);

  // Load groups for a single company
  const loadGroupsForCompany = useCallback(
    async (companyUuid) => {
      setCompanyConfigs((prev) => ({
        ...prev,
        [companyUuid]: { ...prev[companyUuid], groupsLoading: true },
      }));
      const result = await fetchGroupsForCompany(companyUuid);
      setCompanyConfigs((prev) => ({
        ...prev,
        [companyUuid]: {
          ...prev[companyUuid],
          availableGroups: result.data || [],
          groupsLoading: false,
        },
      }));
    },
    [fetchGroupsForCompany]
  );

  // Load groups for all assigned companies on mount
  useEffect(() => {
    for (const [companyUuid, config] of Object.entries(companyConfigs)) {
      if (config.assigned) {
        loadGroupsForCompany(companyUuid);
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompanyToggle = (companyUuid) => {
    setCompanyConfigs((prev) => {
      const current = prev[companyUuid];
      if (current.assigned) {
        // Already assigned — toggle active/inactive instead of removing
        return {
          ...prev,
          [companyUuid]: { ...current, enabled: current.enabled === false },
        };
      }
      // Not yet assigned — assign it and load groups if needed
      if (current.availableGroups.length === 0 && !current.groupsLoading) {
        loadGroupsForCompany(companyUuid);
      }
      return { ...prev, [companyUuid]: { ...current, assigned: true } };
    });
  };

  const handleCompanyConfigChange = (companyUuid, field, value) => {
    setCompanyConfigs((prev) => ({
      ...prev,
      [companyUuid]: { ...prev[companyUuid], [field]: value },
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!personalInfo.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(personalInfo.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!personalInfo.firstName?.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!personalInfo.lastName?.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (
      personalInfo.mobileNumber &&
      !/^[\d\s\-\(\)\+]+$/.test(personalInfo.mobileNumber)
    ) {
      newErrors.mobileNumber = "Invalid phone number format";
    }

    const assignedCompanies = Object.entries(companyConfigs).filter(
      ([, cfg]) => cfg.assigned
    );
    if (assignedCompanies.length === 0) {
      newErrors.companies = "Please assign the user to at least one company.";
    }
    for (const [companyUuid, cfg] of assignedCompanies) {
      if (!cfg.roleUuid) {
        newErrors[`role_${companyUuid}`] = "Role is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      setError("Please fix the errors before saving.");
      return;
    }

    setSaving(true);
    setError("");
    setLicenseErrors([]);
    setLicenseErrorDetails([]);
    setSuccess("");

    const assignedEntries = Object.entries(companyConfigs).filter(
      ([, cfg]) => cfg.assigned
    );
    const deactivateEntries = Object.entries(companyConfigs).filter(
      ([, cfg]) => !cfg.assigned && cfg.userUuid
    );

    // Pre-check license availability for all new active user assignments
    const newActiveAssignments = assignedEntries.filter(
      ([, cfg]) => !cfg.userUuid && cfg.enabled !== false
    );
    if (newActiveAssignments.length > 0 && licenses?.data) {
      let timeNeeded = 0,
        expenseNeeded = 0,
        projectNeeded = 0;

      for (const [companyUuid, cfg] of newActiveAssignments) {
        let resolvedRoleUuid = cfg.roleUuid;
        let role = roles.data.find(
          (r) => r.company_uuid === companyUuid && r.uuid === resolvedRoleUuid
        );
        if (!role) {
          const roleName = roles.data.find(
            (r) => r.uuid === resolvedRoleUuid
          )?.name;
          if (roleName) {
            role = roles.data.find(
              (r) => r.company_uuid === companyUuid && r.name === roleName
            );
          }
        }
        if (!role) continue;

        if (role.enableTime) timeNeeded++;
        if (role.enableExpense) expenseNeeded++;
        if (role.enableProjectManagement) projectNeeded++;
      }

      const licData = licenses.data;
      const violations = [];
      if (timeNeeded > 0 && licData.time?.available < timeNeeded) {
        violations.push({
          name: "Time Entry",
          used: licData.time.used ?? 0,
          total: licData.time.limit ?? 0,
          available: licData.time.available ?? 0,
          needed: timeNeeded,
        });
      }
      if (expenseNeeded > 0 && licData.expenses?.available < expenseNeeded) {
        violations.push({
          name: "Expense",
          used: licData.expenses.used ?? 0,
          total: licData.expenses.limit ?? 0,
          available: licData.expenses.available ?? 0,
          needed: expenseNeeded,
        });
      }
      if (
        projectNeeded > 0 &&
        licData.projectManagement?.available < projectNeeded
      ) {
        violations.push({
          name: "Project Management",
          used: licData.projectManagement.used ?? 0,
          total: licData.projectManagement.limit ?? 0,
          available: licData.projectManagement.available ?? 0,
          needed: projectNeeded,
        });
      }

      if (violations.length > 0) {
        setError("Insufficient license seats to enable this user.");
        setLicenseErrors([]);
        setLicenseErrorDetails(violations);
        setShowLicenseModal(true);
        setSaving(false);
        return;
      }
    }

    let hasLicenseError = false;
    let firstLicenseError = null;
    let anyFailure = false;

    const isAdminScope = sessionStorage.getItem("field_token_scope") === "user";

    try {
      // Process assigned companies (create or update)
      for (const [companyUuid, cfg] of assignedEntries) {
        // Resolve role UUID for this specific company — the stored UUID may belong to a
        // different company (same role name, different UUID). Match by name if needed.
        let resolvedRoleUuid = cfg.roleUuid;
        const roleInCompany = roles.data.find(
          (r) => r.company_uuid === companyUuid && r.uuid === resolvedRoleUuid
        );
        if (!roleInCompany) {
          const roleName = roles.data.find(
            (r) => r.uuid === resolvedRoleUuid
          )?.name;
          if (roleName) {
            const matched = roles.data.find(
              (r) => r.company_uuid === companyUuid && r.name === roleName
            );
            resolvedRoleUuid = matched?.uuid || resolvedRoleUuid;
          }
        }

        const userData = {
          email: personalInfo.email,
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
          mobileNumber: personalInfo.mobileNumber,
          employee: cfg.employee,
          role: resolvedRoleUuid,
          active: cfg.enabled,
          groups: cfg.groups,
        };

        // Always pass companyUuid for admin (user-scoped) tokens so the API validates
        // against the correct managed company, not the admin's own company.
        const targetCompany = isAdminScope ? companyUuid : null;

        let result;
        if (cfg.userUuid) {
          result = await updateUser(cfg.userUuid, userData, targetCompany);
        } else {
          result = await createUser(userData, targetCompany);
        }

        if (!result.success) {
          if (result.status === 403 && result.errors?.length > 0) {
            hasLicenseError = true;
            firstLicenseError = result;
            break;
          } else {
            anyFailure = true;
            setError(
              result.error ||
                `Failed to ${cfg.userUuid ? "update" : "create"} user for ${
                  configureUsersData[companyUuid] || companyUuid
                }.`
            );
            break;
          }
        }
      }

      if (!anyFailure) {
        // Deactivate removed companies
        for (const [companyUuid, cfg] of deactivateEntries) {
          await updateUser(
            cfg.userUuid,
            { active: false },
            isAdminScope ? companyUuid : null
          );
        }
      }

      if (hasLicenseError && firstLicenseError) {
        setError(
          firstLicenseError.error ||
            "Insufficient license seats to enable this user."
        );
        setLicenseErrors(firstLicenseError.errors || []);
        setLicenseErrorDetails(firstLicenseError.details || []);
        setShowLicenseModal(true);
      } else if (!anyFailure) {
        setSuccess(`User ${isEditMode ? "updated" : "created"} successfully!`);
        setTimeout(() => {
          onSave();
        }, 1000);
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel?")) {
      onCancel();
    }
  };

  const assignedCompanyUuids = Object.entries(companyConfigs)
    .filter(([, cfg]) => cfg.assigned)
    .map(([uuid]) => uuid);

  return (
    <div className="user-form">
      <DetailHeader
        title={isEditMode ? "Edit User" : "Add User"}
        onBack={handleCancel}
        onSave={handleSave}
        isEditing={true}
        saveDisabled={saving}
        saveLabel={saving ? "Saving..." : "Save"}
        cancelLabel="Cancel"
      />

      {error && !showLicenseModal && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="danger">{error}</Validation>
        </div>
      )}
      {success && (
        <div style={{ padding: "0 24px", marginTop: "16px" }}>
          <Validation className="success">{success}</Validation>
        </div>
      )}

      <LicenseErrorModal
        isOpen={showLicenseModal}
        onClose={async () => {
          setShowLicenseModal(false);
          await fetchLicenses(true);
        }}
        errorMessage={error}
        details={licenseErrorDetails}
        context="user"
      />

      <div className="user-form__two-col">
        {/* Left Panel */}
        <div className="user-form__left">
          <div className="user-form__section">
            <h3 className="user-form__section-title">Personal Information</h3>
            <div className="user-form__fields">
              <div className="user-form__name-row">
                <div className="user-form__field">
                  <label className="user-form__label">
                    First Name <span className="danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={personalInfo.firstName}
                    onChange={(e) =>
                      handlePersonalInfoChange("firstName", e.target.value)
                    }
                    placeholder="First name"
                    className={`user-form__input${
                      errors.firstName ? " input-error" : ""
                    }`}
                  />
                  {errors.firstName && (
                    <span className="user-form__error">{errors.firstName}</span>
                  )}
                </div>

                <div className="user-form__field">
                  <label className="user-form__label">
                    Last Name <span className="danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={personalInfo.lastName}
                    onChange={(e) =>
                      handlePersonalInfoChange("lastName", e.target.value)
                    }
                    placeholder="Last name"
                    className={`user-form__input${
                      errors.lastName ? " input-error" : ""
                    }`}
                  />
                  {errors.lastName && (
                    <span className="user-form__error">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="user-form__field">
                <label className="user-form__label">
                  Email <span className="danger">*</span>
                </label>
                <input
                  type="email"
                  value={personalInfo.email}
                  onChange={(e) =>
                    handlePersonalInfoChange("email", e.target.value)
                  }
                  placeholder="email@example.com"
                  className={`user-form__input${
                    errors.email ? " input-error" : ""
                  }`}
                />
                {errors.email && (
                  <span className="user-form__error">{errors.email}</span>
                )}
              </div>

              <div className="user-form__field">
                <label className="user-form__label">Mobile Number</label>
                <input
                  type="tel"
                  value={personalInfo.mobileNumber}
                  onChange={(e) =>
                    handlePersonalInfoChange("mobileNumber", e.target.value)
                  }
                  placeholder="Mobile number"
                  className={`user-form__input${
                    errors.mobileNumber ? " input-error" : ""
                  }`}
                />
                {errors.mobileNumber && (
                  <span className="user-form__error">
                    {errors.mobileNumber}
                  </span>
                )}
              </div>
            </div>
          </div>

          {showCompanySection && (
            <div className="user-form__section">
              <h3 className="user-form__section-title">Companies</h3>
              <div className="user-form__company-badges">
                {Object.entries(configureUsersData).map(([uuid, name]) => {
                  const isAssigned = companyConfigs[uuid]?.assigned;
                  const isActive = companyConfigs[uuid]?.enabled !== false;
                  const badgeClass = isAssigned
                    ? isActive
                      ? " user-form__company-badge--assigned"
                      : " user-form__company-badge--assigned-inactive"
                    : "";
                  const code = companies?.codesMap?.[uuid];
                  return (
                    <button
                      key={uuid}
                      type="button"
                      className={`user-form__company-badge${badgeClass}`}
                      onClick={() => handleCompanyToggle(uuid)}
                      disabled={saving}
                    >
                      {code != null ? `${name} (${code})` : name}
                    </button>
                  );
                })}
              </div>
              {errors.companies && (
                <span
                  className="user-form__error"
                  style={{ marginTop: "8px", display: "block" }}
                >
                  {errors.companies}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="user-form__right">
          <h3 className="user-form__panel-title">Per-Company Configuration</h3>
          {assignedCompanyUuids.length === 0 ? (
            <div className="user-form__empty-state">
              Select a company on the left to configure access.
            </div>
          ) : (
            assignedCompanyUuids.map((companyUuid) => {
              const companyName = configureUsersData[companyUuid];
              const code = companies?.codesMap?.[companyUuid];
              const cfg = companyConfigs[companyUuid];
              const companyRoles = roles.data.filter(
                (r) => r.company_uuid === companyUuid
              );
              return (
                <PerCompanyConfig
                  key={companyUuid}
                  companyUuid={companyUuid}
                  companyName={companyName}
                  companyCode={code}
                  config={cfg}
                  availableRoles={companyRoles}
                  onChange={(field, value) =>
                    handleCompanyConfigChange(companyUuid, field, value)
                  }
                  errors={errors}
                  disabled={saving}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
